import { useCallback, useEffect, useState } from "react";
import Header from "./components/Header";
import Chat from "./components/Chat";
import AgentTrace from "./components/AgentTrace";
import ApprovalModal from "./components/ApprovalModal";
import ArchitectureDiagram from "./components/ArchitectureDiagram";
import { useWebSocket } from "./hooks/useWebSocket";
import type { AgentRun, ApprovalRequest, ChatMessage, WSMessage } from "./types";
import { LayoutGrid, Network } from "lucide-react";

const API_BASE = import.meta.env.VITE_WORKER_URL ?? "";

async function createSession(): Promise<string> {
  const res = await fetch(`${API_BASE}/api/sessions`, { method: "POST" });
  const data = (await res.json()) as { sessionId: string };
  return data.sessionId;
}

const SERVICES = [
  { label: "Pages",           dot: "bg-indigo-400" },
  { label: "Workers",         dot: "bg-sky-400" },
  { label: "Durable Objects", dot: "bg-cf-orange" },
  { label: "D1",              dot: "bg-amber-400" },
  { label: "Workflows",       dot: "bg-emerald-400" },
  { label: "AI Gateway",      dot: "bg-pink-400" },
  { label: "Workers AI",      dot: "bg-violet-400" },
  { label: "AI Search",       dot: "bg-cyan-400" },
  { label: "Sandbox",         dot: "bg-slate-400" },
  { label: "R2",              dot: "bg-orange-300" },
  { label: "Human-in-loop",   dot: "bg-yellow-400" },
];

export default function App() {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [runs, setRuns] = useState<Map<string, AgentRun>>(new Map());
  const [approvalRequest, setApprovalRequest] = useState<ApprovalRequest | null>(null);
  const [showArch, setShowArch] = useState(false);
  const [showTrace, setShowTrace] = useState(false);

  const { status, send, onMessage } = useWebSocket(sessionId);

  const isAgentRunning = Array.from(runs.values()).some(
    (r: AgentRun) => r.status === "running" || r.status === "waiting_approval"
  );

  useEffect(() => {
    createSession()
      .then(setSessionId)
      .catch(() => setSessionId(crypto.randomUUID()));
  }, []);

  useEffect(() => {
    return onMessage((msg: WSMessage) => {
      switch (msg.type) {
        case "session_init":
          setSessionId(msg.sessionId);
          break;
        case "history":
          setMessages(msg.messages.map((m) => ({ ...m, timestamp: Date.now() })));
          break;
        case "chat":
          setMessages((prev) => [...prev, { role: "assistant", content: msg.content, timestamp: Date.now() }]);
          break;
        case "agent_step":
          setRuns((prev) => {
            const map = new Map(prev);
            const run = map.get(msg.runId) ?? { id: msg.runId, status: "running" as const, steps: [], query: "" };
            map.set(msg.runId, { ...run, steps: [...run.steps, msg.step] });
            return map;
          });
          break;
        case "approval_request":
          setApprovalRequest({ runId: msg.runId, description: msg.description, details: msg.details });
          setRuns((prev) => {
            const map = new Map(prev);
            const run = map.get(msg.runId);
            if (run) map.set(msg.runId, { ...run, status: "waiting_approval" });
            return map;
          });
          break;
        case "agent_complete":
          if (msg.answer && msg.answer.trim()) {
            setMessages((prev) => [...prev, { role: "assistant", content: msg.answer, timestamp: Date.now() }]);
          }
          setRuns((prev) => {
            const map = new Map(prev);
            const run = map.get(msg.runId);
            if (run) map.set(msg.runId, { ...run, status: "completed" });
            return map;
          });
          break;
        case "agent_error":
          setMessages((prev) => [...prev, { role: "assistant", content: `Agent error: ${msg.error}`, timestamp: Date.now() }]);
          setRuns((prev) => {
            const map = new Map(prev);
            const run = map.get(msg.runId);
            if (run) map.set(msg.runId, { ...run, status: "failed" });
            return map;
          });
          break;
      }
    });
  }, [onMessage]);

  const handleSend = useCallback((content: string) => {
    if (!sessionId) return;
    const runId = crypto.randomUUID();
    setMessages((prev) => [...prev, { role: "user", content, timestamp: Date.now() }]);
    setRuns((prev) => {
      const map = new Map(prev);
      map.set(runId, { id: runId, status: "running", steps: [], query: content });
      return map;
    });
    send({ type: "chat", content, runId });

    // Safety timeout: if no response in 35s (AI hard-fails at 30s), unblock the UI
    setTimeout(() => {
      setRuns((prev) => {
        const run = prev.get(runId);
        if (run && (run.status === "running" || run.status === "waiting_approval")) {
          const map = new Map(prev);
          map.set(runId, { ...run, status: "failed" });
          setMessages((m) => [...m, {
            role: "assistant",
            content: "Request timed out. The agent took too long to respond. Please try again.",
            timestamp: Date.now(),
          }]);
          return map;
        }
        return prev;
      });
    }, 35_000);
  }, [sessionId, send]);

  const handleCancel = useCallback(() => {
    const runningRun = Array.from(runs.values()).find(
      (r) => r.status === "running" || r.status === "waiting_approval"
    );
    if (runningRun) {
      send({ type: "cancel", runId: runningRun.id });
      setRuns((prev) => {
        const map = new Map(prev);
        map.set(runningRun.id, { ...runningRun, status: "cancelled" });
        return map;
      });
    }
  }, [runs, send]);

  const handleApprove = useCallback((feedback?: string) => {
    if (!approvalRequest) return;
    send({ type: "approval_response", runId: approvalRequest.runId, approved: true, feedback });
    setApprovalRequest(null);
    setRuns((prev) => {
      const map = new Map(prev);
      const run = map.get(approvalRequest.runId);
      if (run) map.set(approvalRequest.runId, { ...run, status: "running" });
      return map;
    });
  }, [approvalRequest, send]);

  const handleReject = useCallback((feedback?: string) => {
    if (!approvalRequest) return;
    send({ type: "approval_response", runId: approvalRequest.runId, approved: false, feedback });
    setApprovalRequest(null);
  }, [approvalRequest, send]);

  const runsArray = Array.from(runs.values()).slice(-4).reverse();

  return (
    <div className="flex flex-col h-screen bg-dark-bg overflow-hidden">
      <Header wsStatus={status} sessionId={sessionId} />

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* ── Left: Chat panel ──────────────────────────────────── */}
        <div className="flex flex-col w-full md:w-1/3 flex-shrink-0 border-r border-dark-border min-h-0">
          <Chat messages={messages} isAgentRunning={isAgentRunning} onSend={handleSend} onCancel={handleCancel} />
        </div>

        {/* ── Right: Dashboard ──────────────────────────────────── */}
        <div className="hidden md:flex flex-col flex-1 min-h-0 overflow-y-auto scrollbar-thin">
          {/* Dashboard header bar */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-dark-border bg-dark-surface flex-shrink-0">
            <div className="flex items-center gap-2">
              <LayoutGrid className="w-3.5 h-3.5 text-cf-orange" />
              <span className="text-[11px] font-mono font-semibold uppercase tracking-widest text-slate-500">
                Agent Dashboard
              </span>
            </div>
            <button
              onClick={() => setShowArch((v) => !v)}
              className="flex items-center gap-1.5 text-[11px] font-mono text-slate-500 hover:text-cf-orange transition-colors"
            >
              <Network className="w-3.5 h-3.5" />
              {showArch ? "Hide" : "Show"} architecture
            </button>
          </div>

          <div className="flex-1 p-4 space-y-4">
            {/* Architecture diagram */}
            {showArch && <ArchitectureDiagram />}

            {/* Service badges */}
            <div className="flex flex-wrap gap-1.5">
              {SERVICES.map((s) => (
                <span
                  key={s.label}
                  className="inline-flex items-center gap-1.5 text-[11px] font-mono px-2.5 py-1 rounded-full bg-dark-surface border border-dark-border text-slate-400 hover:border-dark-border-2 transition-colors"
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                  {s.label}
                </span>
              ))}
            </div>

            {/* Agent trace — collapsed by default */}
            {runsArray.length > 0 && (
              <div>
                <button
                  onClick={() => setShowTrace((v) => !v)}
                  className="flex items-center gap-2 text-[11px] font-mono text-slate-500 hover:text-cf-orange transition-colors mb-2"
                >
                  <span className={`transition-transform ${showTrace ? 'rotate-90' : ''}`}>▶</span>
                  {showTrace ? 'Hide' : 'Show'} agent reasoning trace ({runsArray.reduce((n, r) => n + r.steps.length, 0)} steps)
                </button>
                {showTrace && <AgentTrace runs={runsArray} />}
              </div>
            )}
          </div>
        </div>
      </div>

      {approvalRequest && (
        <ApprovalModal request={approvalRequest} onApprove={handleApprove} onReject={handleReject} />
      )}
    </div>
  );
}
